// src/lib/env.ts — boot-time environment validation.
// Plan ref: 05-RESEARCH-ui-shell.md §Code Examples §Env validation.
// Throws synchronously on invalid env so a misconfigured build never reaches users.
//
// Observability vars (Sentry + PostHog) are OPTIONAL and build-time only —
// see studio/ui/README.md §Environment. CI injects them as `VITE_*`; an
// unset OR empty value means "SDK off" (an empty GitHub secret inlines as
// `""`, so `""` MUST NOT fail validation — it is normalised to undefined).

import { z } from 'zod';

const emptyToUndefined = (v: unknown): unknown => (v === '' ? undefined : v);

const Env = z.object({
  VITE_SUPABASE_URL: z.string().url(),
  VITE_SUPABASE_ANON_KEY: z.string().min(20),
  VITE_SENTRY_DSN: z.preprocess(emptyToUndefined, z.string().url().optional()),
  VITE_SENTRY_ENV: z.preprocess(emptyToUndefined, z.string().optional()),
  VITE_POSTHOG_KEY: z.preprocess(emptyToUndefined, z.string().optional()),
  VITE_GIT_SHA: z.preprocess(emptyToUndefined, z.string().optional()),
  MODE: z.string().default('production'),
});

const parsed = Env.safeParse(import.meta.env);
if (!parsed.success) {
  console.error('Invalid env:', parsed.error.flatten().fieldErrors);
  throw new Error('Studio UI env validation failed');
}

export const env = {
  SUPABASE_URL: parsed.data.VITE_SUPABASE_URL,
  SUPABASE_ANON_KEY: parsed.data.VITE_SUPABASE_ANON_KEY,
  /** Sentry DSN — SDK is initialised ONLY when this is non-empty. */
  SENTRY_DSN: parsed.data.VITE_SENTRY_DSN,
  /** Sentry `environment` tag; falls back to Vite's MODE (production|development). */
  SENTRY_ENV: parsed.data.VITE_SENTRY_ENV ?? parsed.data.MODE,
  /** PostHog project API key (public `phc_…`) — SDK is initialised ONLY when non-empty. */
  POSTHOG_KEY: parsed.data.VITE_POSTHOG_KEY,
  /** Git SHA baked in at build time → Sentry release + PostHog super property. */
  GIT_SHA: parsed.data.VITE_GIT_SHA,
  MODE: parsed.data.MODE,
};
